import { useEffect, useRef } from 'react';

import { useChatAnalytics } from '@/hooks/chat/useChatAnalytics';
import {
  trackExtractedStatementsRendered,
  type ExtractedStatementsViewerRole,
} from '@/util/analyticsEvents';
import { shouldFireImpression } from '@/util/impressionDedup';

interface UseExtractedStatementsImpressionParams {
  messageSid: string;
  statementCount: number;
  /** Published references the viewer is shown on this message. Emitted on
   * the impression event so the recipient-side "references shown" funnel
   * has a denominator. Defaults to 0 for callers that don't supply it. */
  referenceCount?: number;
  /** Distinct statements carrying at least one shown reference. Defaults
   * to 0. */
  statementsWithReferences?: number;
  /** Viewer's role relative to the author. Required (no default): there is
   * no safe fallback — defaulting either way would silently mislabel the
   * impression and poison the recipient-side denominator this event feeds.
   * The bubble derives it from message authorship. */
  viewerRole: ExtractedStatementsViewerRole;
}

/**
 * Reports that a statement answer was shown. The
 * `EXTRACTED_STATEMENTS_RENDERED` impression fires once per message
 * per session and captures the reference metadata as of the first render.
 * `parseMessage` hydrates `attachedStatementReferences` synchronously when
 * messages are fetched, so an already-published message renders with its
 * references on the first frame and the captured values are accurate.
 * References published live while the recipient is watching are
 * deliberately not re-counted as a fresh impression — the impression
 * records what was shown when the answer first appeared.
 */
export function useExtractedStatementsImpression({
  messageSid,
  statementCount,
  referenceCount = 0,
  statementsWithReferences = 0,
  viewerRole,
}: UseExtractedStatementsImpressionParams): void {
  const { track } = useChatAnalytics();

  /**
   * First-render snapshot of the impression payload. Initialized once (React
   * keeps the first render's value), so the impression effect can depend only
   * on `[messageSid, track]` without claiming reactivity to reference counts
   * it intentionally does not re-fire on.
   */
  const impressionPayloadRef = useRef({
    statementCount,
    referenceCount,
    statementsWithReferences,
    viewerRole,
  });

  useEffect(() => {
    if (!shouldFireImpression(`extracted-statements:${messageSid}`)) {
      return;
    }

    trackExtractedStatementsRendered(track, {
      messageSid,
      ...impressionPayloadRef.current,
    });
  }, [messageSid, track]);
}
